import * as d3 from 'd3';
import { numberToLocaleString } from '@/lib/utils';


//Helpers
const translate = (x: number, y: number) => `translate(${x}, ${y})`;
const hourMs = 60 * 60 * 1000;

export type PlayerCountDatum = {
    date: Date;
    min: number;
    avg: number;
    max: number;
};

type DrawPlayerCountProps = {
    svgRef: SVGElement;
    setRenderError: (error: string) => void;
    size: {
        width: number;
        height: number;
    };
    margins: {
        top: number;
        right: number;
        bottom: number;
        left: number;
        axis: number;
    };
    domain: [Date, Date];
    bucketMs: number;
    data: PlayerCountDatum[];
    onHover: (datum: PlayerCountDatum | undefined) => void;
};

export default function drawPlayerCount({
    svgRef,
    setRenderError,
    size: { width, height },
    margins,
    domain,
    bucketMs,
    data,
    onHover,
}: DrawPlayerCountProps) {
    //Clear
    const svg = d3.select(svgRef);
    svg.selectAll('*').remove();
    if (!width || !height || !data.length) return;

    try {
        const drawableAreaHeight = height - margins.top - margins.bottom;
        const drawableAreaWidth = width - margins.left - margins.right;
        if (drawableAreaHeight < 20 || drawableAreaWidth < 40) return;

        const chartGroup = svg.append('g')
            .attr('transform', translate(margins.left, margins.top));

        //every bucket gets an equal slot and sits in the middle of it, so the first
        //and last one stay inside the drawable area instead of bleeding into the margins
        const bucketCount = Math.max(1, Math.round((domain[1].getTime() - domain[0].getTime()) / bucketMs) + 1);
        const bucketWidth = drawableAreaWidth / bucketCount;

        //Scales
        const timeScale = d3.scaleTime()
            .domain(domain)
            .range([bucketWidth / 2, drawableAreaWidth - bucketWidth / 2]);
        const observedMax = d3.max(data, (d) => d.max) ?? 0;
        const countScale = d3.scaleLinear()
            .domain([0, Math.max(observedMax, 1)])
            .nice()
            .range([drawableAreaHeight, 0]);

        //Axis - no ticks or baselines, the dashed grid carries the reading
        const countTickCount = height > 220 ? 6 : 4;
        const timeAxisTicksScale = d3.scaleLinear([382, 1350], [4, 10]);
        const timeAxis = d3.axisBottom(timeScale)
            .ticks(Math.round(timeAxisTicksScale(width)))
            .tickSize(0)
            .tickPadding(8);
        chartGroup.append('g')
            .attr('transform', translate(0, drawableAreaHeight))
            .attr('class', 'time-axis')
            .call(timeAxis)
            .call((g) => g.select('.domain').remove());

        //these are player counts, so fractional ticks would just repeat the same label
        const countScaleMax = countScale.domain()[1];
        const countTicks = countScaleMax <= 7
            ? d3.range(countScaleMax + 1)
            : countScale.ticks(countTickCount);
        const countAxis = d3.axisLeft(countScale)
            .tickValues(countTicks)
            .tickSize(0)
            .tickPadding(8)
            .tickFormat((d) => numberToLocaleString(d as number));
        svg.append('g')
            .attr('class', 'counts-axis')
            .attr('transform', translate(margins.left - margins.axis, margins.top))
            .call(countAxis)
            .call((g) => g.select('.domain').remove());

        //Horizontal grid, same dashed hairlines as the sanction stats chart
        chartGroup.append('g')
            .selectAll('line')
            .data(countTicks)
            .join('line')
            .attr('class', 'stroke-edge')
            .attr('stroke-dasharray', '8 6')
            .attr('x1', 0)
            .attr('x2', drawableAreaWidth)
            .attr('y1', (d) => Math.round(countScale(d)) + 0.5)
            .attr('y2', (d) => Math.round(countScale(d)) + 0.5);

        //The band between the quietest and busiest minute of each bucket. Over a whole
        //day or week the quietest minute is always around zero and the band would just
        //fill the chart, so there it starts at the average instead
        const bandFloor = bucketMs > hourMs
            ? (d: PlayerCountDatum) => countScale(d.avg)
            : (d: PlayerCountDatum) => countScale(d.min);
        const rangeArea = d3.area<PlayerCountDatum>()
            .x((d) => timeScale(d.date))
            .y0(bandFloor)
            .y1((d) => countScale(d.max))
            .curve(d3.curveMonotoneX);
        chartGroup.append('path')
            .datum(data)
            .attr('class', 'fill-accent/20')
            .attr('d', rangeArea);

        const avgLine = d3.line<PlayerCountDatum>()
            .x((d) => timeScale(d.date))
            .y((d) => countScale(d.avg))
            .curve(d3.curveMonotoneX);
        chartGroup.append('path')
            .datum(data)
            .attr('fill', 'none')
            .attr('stroke-width', 1.5)
            .attr('class', 'stroke-accent')
            .attr('d', avgLine);

        //Hover readout
        const cursorGroup = chartGroup.append('g').attr('opacity', 0);
        cursorGroup.append('rect')
            .attr('class', 'interaction-highlight')
            .attr('y', 0)
            .attr('width', Math.max(2, bucketWidth))
            .attr('height', drawableAreaHeight);
        cursorGroup.append('line')
            .attr('class', 'stroke-edge-strong')
            .attr('y1', 0)
            .attr('y2', drawableAreaHeight);
        cursorGroup.append('circle')
            .attr('r', 3)
            .attr('class', 'fill-accent');

        const bisectDate = d3.bisector<PlayerCountDatum, Date>((d) => d.date).center;
        chartGroup.append('rect')
            .attr('width', drawableAreaWidth)
            .attr('height', drawableAreaHeight)
            .attr('fill', 'transparent')
            .attr('cursor', 'crosshair')
            .on('mousemove', (event) => {
                const [pointerX] = d3.pointer(event);
                const datum = data[bisectDate(data, timeScale.invert(pointerX))];
                if (!datum) return;
                const x = timeScale(datum.date);
                cursorGroup.attr('opacity', 1)
                    .select('line').attr('x1', x).attr('x2', x);
                cursorGroup.select('rect')
                    .attr('x', x - Math.max(2, bucketWidth) / 2);
                cursorGroup.select('circle')
                    .attr('cx', x)
                    .attr('cy', countScale(datum.avg));
                onHover(datum);
            })
            .on('mouseleave', () => {
                cursorGroup.attr('opacity', 0);
                onHover(undefined);
            });
    } catch (error) {
        setRenderError((error as Error).message ?? 'unknown error');
    }
}
